import { Container } from 'pixi.js';
import { DepthSorter } from './DepthSorter';
import { boxContainsScreenPoint } from './WorldItem';
/**
 * The depth-sorted "world" layer: walls, objects and characters.
 *
 * Its container is its own Pixi render group, so re-ordering it (which
 * forces Pixi to rebuild this group's instruction list) never touches the
 * static floor group. Each frame:
 *   1. cull: items whose rect misses the view are hidden (visible = false),
 *   2. sort: only on-screen items go through the DepthSorter,
 *   3. apply: children are rewritten in place (on-screen sorted first,
 *      hidden ones after) and the render group is flagged only if the
 *      order actually changed.
 */
export class WorldLayer {
    container;
    items = [];
    sorter = new DepthSorter();
    /** On-screen items in back-to-front order after the last update (for picking). */
    drawList = [];
    onScreenTmp = [];
    stats = { total: 0, onScreen: 0, sortMs: 0, pairTests: 0, edges: 0, reordered: false };
    constructor() {
        this.container = new Container({ label: 'world', isRenderGroup: true });
    }
    add(item) {
        this.items.push(item);
        this.container.addChild(item.view);
    }
    remove(item) {
        const i = this.items.indexOf(item);
        if (i >= 0)
            this.items.splice(i, 1);
        const d = this.drawList.indexOf(item);
        if (d >= 0)
            this.drawList.splice(d, 1);
        this.container.removeChild(item.view);
    }
    get count() {
        return this.items.length;
    }
    forEach(cb) {
        for (let i = 0; i < this.items.length; i++)
            cb(this.items[i]);
    }
    update(view) {
        const t0 = performance.now();
        const items = this.items;
        const onScreen = this.onScreenTmp;
        onScreen.length = 0;
        const sorter = this.sorter;
        sorter.begin();
        for (let i = 0; i < items.length; i++) {
            const it = items[i];
            const vis = it.right > view.left && it.left < view.right && it.bottom > view.top && it.top < view.bottom;
            if (vis !== it.onScreen) {
                it.onScreen = vis;
                it.view.visible = vis;
            }
            if (vis) {
                onScreen.push(it);
                sorter.add(it.box, it.left, it.top, it.right, it.bottom, it.id);
            }
        }
        const order = sorter.sort();
        // Rewrite children in place; detect whether anything moved.
        const children = this.container.children;
        let changed = children.length !== items.length;
        const draw = this.drawList;
        draw.length = onScreen.length;
        let k = 0;
        for (let r = 0; r < onScreen.length; r++) {
            const it = onScreen[order[r]];
            draw[r] = it;
            if (children[k] !== it.view) {
                children[k] = it.view;
                changed = true;
            }
            k++;
        }
        for (let i = 0; i < items.length; i++) {
            const it = items[i];
            if (it.onScreen)
                continue;
            if (children[k] !== it.view) {
                children[k] = it.view;
                changed = true;
            }
            k++;
        }
        if (changed) {
            // Same mechanism Container.sortChildren() relies on: rebuild this group's instructions.
            this.container.renderGroup.structureDidChange = true;
        }
        const s = this.stats;
        s.total = items.length;
        s.onScreen = onScreen.length;
        s.pairTests = sorter.pairTests;
        s.edges = sorter.edgeCount;
        s.reordered = changed;
        s.sortMs = performance.now() - t0;
    }
    /** Front-most pickable item under a world-screen point, using exact box silhouettes. */
    pick(wx, wy, filter) {
        const draw = this.drawList;
        for (let i = draw.length - 1; i >= 0; i--) {
            const it = draw[i];
            if (!it.pickable)
                continue;
            if (wx < it.left || wx > it.right || wy < it.top || wy > it.bottom)
                continue;
            if (filter && !filter(it))
                continue;
            if (boxContainsScreenPoint(it.box, wx, wy, it.kind === 'wall' ? 0.08 : 0.02))
                return it;
        }
        return null;
    }
}
